import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { Section } from "../components/Section";
import { Seo } from "../components/Seo";
import { SocialShare } from "../components/SocialShare";
import { PublicNewsletter, getPublicNewsletters } from "../data/cms";
import { NewsItem, getNews } from "../data/news";

export function News() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [newsletters, setNewsletters] = useState<PublicNewsletter[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([getNews({ useFallback: false }), getPublicNewsletters()])
      .then(([newsItems, newsletterItems]) => {
        setItems(newsItems);
        setNewsletters(newsletterItems);
      })
      .catch((err) => {
        console.warn("[public-web] News page load failed", err);
        setError("News could not be loaded right now.");
      })
      .finally(() => setLoading(false));
  }, []);

  const queryText = search.trim().toLowerCase();
  const filteredNewsletters = newsletters.filter((item) => {
    if (!queryText) return true;
    return `${item.title} ${item.summary} ${item.attachmentName || ""}`.toLowerCase().includes(queryText);
  });
  const filteredItems = items.filter((item) => {
    if (!queryText) return true;
    return `${item.title} ${item.summary} ${item.body || ""} ${item.category}`.toLowerCase().includes(queryText);
  });

  return (
    <>
      <Seo title="News" description="Latest school news and newsletter archives from Zion Classical Academy." path="/news" />
      <PageHeader eyebrow="News" title="Latest school news">
        Current school updates and downloadable newsletter archives.
      </PageHeader>
      <Section title="Find an update">
        <div className="search-panel">
          <label>
            Search news and newsletters
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by title, topic, or file name" />
          </label>
          <SocialShare title="Zion Classical Academy News" path="/news" label="Share this page" />
        </div>
      </Section>
      <Section title="Newsletter Archive">
        <div className="list-stack">
          {loading ? <p className="lead">Loading newsletters...</p> : null}
          {!loading && newsletters.length === 0 ? <p className="lead">No public newsletters are available right now.</p> : null}
          {!loading && newsletters.length > 0 && filteredNewsletters.length === 0 ? <p className="lead">No newsletters matched your search.</p> : null}
          {filteredNewsletters.map((item) => (
            <article className="list-item" key={item.id}>
              <p className="eyebrow">{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : "Recent"}</p>
              <h3>{item.title}</h3>
              <p>{item.summary}</p>
              {item.attachmentUrl ? (
                <div className="attachment-list">
                  <a href={item.attachmentUrl} target="_blank" rel="noreferrer">
                    {item.attachmentName || "Open newsletter"}
                  </a>
                </div>
              ) : null}
              <SocialShare title={item.title} path="/news" />
            </article>
          ))}
        </div>
      </Section>
      <Section title="Updates">
        <div className="list-stack">
          {loading ? <p className="lead">Loading news...</p> : null}
          {!loading && error ? <p className="notice-light">{error}</p> : null}
          {!loading && !error && items.length === 0 ? <p className="lead">No published news is available right now.</p> : null}
          {!loading && !error && items.length > 0 && filteredItems.length === 0 ? <p className="lead">No news matched your search.</p> : null}
          {filteredItems.map((item) => (
            <article className="list-item" key={item.id}>
              <p className="eyebrow">
                {item.urgent ? "Urgent · " : ""}
                {item.category} · {item.date ? new Date(item.date).toLocaleDateString() : "Recent"}
              </p>
              <h3><Link to={`/news/${item.id}`}>{item.title}</Link></h3>
              <p>{item.summary}</p>
              {item.body ? <p>{item.body}</p> : null}
              {item.linkUrl ? <a className="text-link" href={item.linkUrl}>Open link</a> : null}
              {item.attachments?.length ? (
                <div className="attachment-list">
                  {item.attachments.map((attachment) => (
                    attachment.downloadUrl ? (
                      <a key={attachment.id} href={attachment.downloadUrl}>{attachment.name}</a>
                    ) : (
                      <span key={attachment.id}>{attachment.name}</span>
                    )
                  ))}
                </div>
              ) : null}
              <SocialShare title={item.title} path={`/news/${item.id}`} />
            </article>
          ))}
        </div>
      </Section>
    </>
  );
}
